import React, { useMemo, useState } from 'react';
import { useDrag } from 'react-dnd';
import { SourceType } from 'dnd-core';
import { prepareProps } from '../../utils/propUtils';
import { PropsWithoutChildren } from '../../utils/utilityTypes';

export type SortableListItemProps<T> = PropsWithoutChildren<{
  item: T;
  dndType: SourceType;
  renderItem: RenderSortableListItem<T>;
}>;

export const SortableListItem = React.memo((props) => {
  const { item, dndType, renderItem } = prepareProps({
    props,
    ignoreChildren: true
  });

  const [element, setElement] = useState<HTMLElement | null>(null);

  const [dragItem] = useState<DragItem<any>>({ item, clientHeight: 0 });
  dragItem.item = item;
  dragItem.clientHeight = element?.clientHeight ?? 0;

  const [{ isDragging }, dragRef] = useDrag({
    type: dndType,
    item: dragItem,
    collect: (monitor) => {
      return {
        isDragging: monitor.isDragging()
      };
    }
  });

  return useMemo(() => {
    return renderItem({
      item,
      ref: (e) => {
        dragRef(e);
        setElement(e);
      },
      style: {
        opacity: isDragging ? 0.5 : 1
      }
    });
  }, [dragRef, isDragging, item, renderItem]);
}) as <T>(props: SortableListItemProps<T>) => JSX.Element;

export type RenderSortableListItem<T> = (
  options: RenderSortableListItemOptions<T>
) => JSX.Element;
export type RenderSortableListItemOptions<T> = {
  item: T;
  ref: (element: HTMLElement | null) => void;
  style: { opacity: number };
};

export type DragItem<T> = { item: T; clientHeight: number };
